import {
  ComponentsWithoutCustomerInput,
  ComponentTypes,
} from '../Data/Components';
import ValidationStatus from '../Data/ValidationStatus';
import { getComponentValidationStatus } from '../Components/__helpers__/component.helpers';

export const RadioChainDisplayState = {
  CURRENT: 'current',
  COLLAPSED: 'collapsed',
  HIDDEN: 'hidden',
};

// The client-api component hosts the booking journey rather than asking a
// question, so it never takes part in (or breaks) a page's chain.
const ComponentsOutsideProgressiveDisplay = [
  ...ComponentsWithoutCustomerInput,
  ComponentTypes.CLIENT_API,
];

const getVisibleInputComponents = (components) => {
  return components.filter(
    (component) =>
      !ComponentsOutsideProgressiveDisplay.includes(component.component.type) &&
      !component.input?.hiddenInChameleon
  );
};

const hasConditionality = (component) => {
  const conditional = component?.component?.conditional;
  if (!conditional) return false;
  return Object.values(conditional).some(
    (condition) => Array.isArray(condition) && condition.length > 0
  );
};

const ProgressiveDisplayInputTypes = [
  ComponentTypes.TEXTFIELD,
  ComponentTypes.RADIOS,
];

/**
 * The one input type every field on the page shares, or undefined when the
 * page mixes types or uses one progressive display does not support.
 */
const getPageInputType = (fields) => {
  const inputType = fields[0]?.component?.type;
  if (!ProgressiveDisplayInputTypes.includes(inputType)) return undefined;

  const isUniform = fields.every(
    (component) => component.component.type === inputType
  );
  return isUniform ? inputType : undefined;
};

/**
 * Filters input components to only those eligible for progressive display.
 * Returns empty array if any component disqualifies the page.
 *
 * @param {Array} fields - Array of visible input component objects
 * @returns {Array} The fields when the page is all text fields or all radios and every one is required, or empty array if page is disqualified
 */
export const getEligibleProgressiveDisplayFields = (fields) => {
  if (!getPageInputType(fields)) {
    return [];
  }

  // Any visible input not required → not eligible
  if (fields.some(({ validation }) => !validation?.constraints?.required)) {
    return [];
  }

  return fields;
};

function findProgressiveDisplayChainData(eligibleFields) {
  let componentsChain = [];
  let trigger = null;

  eligibleFields.forEach(({ component }, index, fields) => {
    const componentHasProgressiveDisplaySet =
      !!component.hideUntilPreviousQuestionAnswered;

    if (componentHasProgressiveDisplaySet && !trigger) {
      componentsChain.push(component.id);

      trigger = fields[index - 1]?.component?.id;

      return;
    }

    if (componentHasProgressiveDisplaySet && trigger) {
      componentsChain.push(component.id);

      return;
    }

    componentsChain = [];
    trigger = null;
  });

  return {
    trigger,
    componentsChain,
  };
}

/**
 * Builds progressive display configuration for all pages in the form.
 * Progressive display allows fields to be revealed one-at-a-time as the user fills them.
 *
 * @param {Object} components - Redux components state object (keyed by component_${id})
 * @param {Object} paging - Redux paging state with pages object { [pageNum]: [componentIds] }
 * @returns {Object} Configuration object with structure:
 *   { pages: { [pageNum]: { trigger, componentsChain, revealedFields, inputType, reopenedFields? } } }
 *   reopenedFields is added by the reopenField reducer on the first Edit.
 *   Returns { pages: {} } if no pages qualify for progressive display
 */
export const buildProgressiveDisplayConfig = (components, paging) => {
  const pages = {};
  Object.entries(paging.pages).forEach(([pageNumber, componentIds]) => {
    const pageComponents = componentIds
      .map((id) => components[`component_${id}`])
      .filter(Boolean);

    if (pageComponents.some(hasConditionality)) {
      return;
    }

    const inputComponents = getVisibleInputComponents(pageComponents);

    if (inputComponents.length < 2) {
      return;
    }

    if (inputComponents[0].component.hideUntilPreviousQuestionAnswered) {
      return;
    }

    const eligibleFields = getEligibleProgressiveDisplayFields(inputComponents);
    if (eligibleFields.length === 0) {
      return;
    }

    const { trigger, componentsChain } =
      findProgressiveDisplayChainData(eligibleFields);

    if (!trigger) {
      return;
    }

    pages[pageNumber] = {
      trigger,
      componentsChain,
      revealedFields: [],
      inputType: getPageInputType(eligibleFields),
    };
  });

  return { pages };
};

// A chosen option is an answer straight away. "Other" writes every keystroke
// into the value, so it only counts once the customer has committed the text
// (Enter or leaving the field) and the radio has validated.
const isRadioAnswered = (component) => {
  const selectedOptionId = component?.input?.selectedOptions?.[0];
  if (selectedOptionId === undefined) return false;
  if (selectedOptionId === 'other-option') {
    return (
      getComponentValidationStatus({ component }) === ValidationStatus.VALID
    );
  }
  // A selection the radio no longer offers is stale state, not an answer.
  return (component.input.options ?? []).some(
    (option) => String(option.id) === String(selectedOptionId)
  );
};

/**
 * Where a radio sits in its page's chain: an answered question is `collapsed`;
 * an unanswered one is `current` when it is the first unanswered or the
 * customer reopened it with Edit, otherwise `hidden`. Derived from the answers
 * plus the reopened list, so editing an earlier answer reopens only that
 * question while the other answered ones stay collapsed, and editing a second
 * one keeps both open. Null when the component is not in a radios chain.
 */
export const getRadioChainDisplayState = ({
  pageConfig,
  components,
  componentId,
}) => {
  if (pageConfig?.inputType !== ComponentTypes.RADIOS) return null;

  const chain = [pageConfig.trigger, ...pageConfig.componentsChain];
  if (!chain.includes(componentId)) return null;

  const isAnswered = (id) => isRadioAnswered(components[`component_${id}`]);
  if (isAnswered(componentId)) return RadioChainDisplayState.COLLAPSED;

  // Reopened questions sit alongside the flow rather than taking it over: the
  // next question in line stays open and numbered while they are re-answered.
  const reopenedFields = pageConfig.reopenedFields ?? [];
  const currentId = chain.find(
    (id) => !isAnswered(id) && !reopenedFields.includes(id)
  );
  if (componentId === currentId || reopenedFields.includes(componentId)) {
    return RadioChainDisplayState.CURRENT;
  }
  return RadioChainDisplayState.HIDDEN;
};

/**
 * Whether a page's progressive display currently keeps this component off the
 * page, whichever chain model the page uses.
 */
export const isHiddenByProgressiveDisplay = ({
  pageConfig,
  components,
  componentId,
}) => {
  if (!pageConfig) return false;

  if (pageConfig.inputType === ComponentTypes.RADIOS) {
    return (
      getRadioChainDisplayState({ pageConfig, components, componentId }) ===
      RadioChainDisplayState.HIDDEN
    );
  }

  return (
    pageConfig.componentsChain.includes(componentId) &&
    !pageConfig.revealedFields.includes(componentId)
  );
};
